// Writes to the two task collections. Every write sets updatedBy and
// updatedAt. A private task lives under users/{uid}/privateTasks and is never
// written under households/.

import {
  Timestamp,
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  type DocumentReference,
} from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { computeRemindAt } from '@/logic/reminders'
import type { DateStr, Recurrence, Scope, Task, TaskDoc, TaskKind, TimeStr } from '@/types'

export interface Paths {
  hid: string
  uid: string
}

export function taskCollection(p: Paths, scope: Scope) {
  return scope === 'shared' ? collection(db, 'households', p.hid, 'tasks') : collection(db, 'users', p.uid, 'privateTasks')
}

export function taskRef(p: Paths, scope: Scope, id: string): DocumentReference {
  return doc(taskCollection(p, scope), id)
}

export function newTaskId(p: Paths, scope: Scope): string {
  return doc(taskCollection(p, scope)).id
}

export interface NewTaskInput {
  title: string
  scope: Scope
  kind?: TaskKind
  categoryId?: string | null
  /** The Do it on day, from the day it was added to. A routine's day in its week. */
  plannedDate?: DateStr | null
  /** A routine's week: the Monday of the week it was added in. */
  weekOf?: DateStr | null
  order: number
  /** A task made from an event starts with its notes, due date and time, and pattern. */
  description?: string | null
  dueDate?: DateStr | null
  dueTime?: TimeStr | null
  recurrence?: Recurrence | null
  /** Who's doing a shared task; ignored on a private one, which has no assignee. */
  assigneeUid?: string | null
}

/** The fields a brand-new task carries: a title, where it was added, and nulls, or what an event gave it. */
export function blankTask(input: NewTaskInput, uid: string): Omit<TaskDoc, 'createdAt' | 'updatedAt'> {
  const kind = input.kind ?? 'once'
  const base: Omit<TaskDoc, 'createdAt' | 'updatedAt'> = {
    title: input.title.trim().slice(0, 200),
    description: input.description ?? null,
    kind,
    order: input.order,
    categoryId: input.categoryId ?? null,
    checklist: [],
    plannedDate: input.plannedDate ?? null,
    dueDate: input.dueDate ?? null,
    dueTime: input.dueTime ?? null,
    done: false,
    doneOn: null,
    completedAt: null,
    waiting: false,
    followUpDate: null,
    followUpTime: null,
    recurrence: kind === 'repeat' ? (input.recurrence ?? null) : null,
    weekOf: kind === 'routine' ? (input.weekOf ?? null) : null,
    completions: [],
    reminder: null,
    remindAt: null,
    createdBy: uid,
    updatedBy: uid,
  }
  if (input.scope === 'shared') base.assigneeUid = input.assigneeUid ?? null
  return base
}

/**
 * Creates the task and returns its id at once: the write applies locally
 * immediately and reaches the server when there is a connection. The returned
 * promise settles on the server's acknowledgement.
 */
export function addTask(p: Paths, input: NewTaskInput): { id: string; written: Promise<void> } {
  const id = newTaskId(p, input.scope)
  const written = setDoc(taskRef(p, input.scope, id), {
    ...blankTask(input, p.uid),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })
  return { id, written }
}

export type TaskPatch = Partial<
  Pick<
    TaskDoc,
    | 'title'
    | 'description'
    | 'kind'
    | 'order'
    | 'categoryId'
    | 'assigneeUid'
    | 'checklist'
    | 'plannedDate'
    | 'dueDate'
    | 'dueTime'
    | 'done'
    | 'doneOn'
    | 'completedAt'
    | 'waiting'
    | 'followUpDate'
    | 'followUpTime'
    | 'recurrence'
    | 'weekOf'
    | 'completions'
    | 'reminder'
  >
>

const REMIND_KEYS: (keyof TaskPatch)[] = ['kind', 'done', 'dueDate', 'dueTime', 'reminder', 'waiting', 'followUpDate', 'followUpTime']

/**
 * Applies a patch in one write. Ticking a one-off stamps the server's time;
 * remindAt is recomputed whenever anything it depends on changes.
 */
export async function updateTask(p: Paths, task: Task, patch: TaskPatch): Promise<void> {
  const data: Record<string, unknown> = { ...patch, updatedBy: p.uid, updatedAt: serverTimestamp() }
  if (task.scope === 'private' && 'assigneeUid' in data) delete data.assigneeUid
  if (patch.done === true && !('completedAt' in patch)) data.completedAt = serverTimestamp()
  if (REMIND_KEYS.some((k) => k in patch)) {
    const at = computeRemindAt({ ...task, ...patch })
    data.remindAt = at ? Timestamp.fromDate(at) : null
  }
  await updateDoc(taskRef(p, task.scope, task.id), data)
}

export async function deleteTask(p: Paths, task: Task): Promise<void> {
  await deleteDoc(taskRef(p, task.scope, task.id))
}

/** Recreates a deleted task with the same id, for Undo. */
export async function restoreTask(p: Paths, task: Task): Promise<void> {
  const data = stripLocal(task)
  await setDoc(taskRef(p, task.scope, task.id), { ...data, updatedBy: p.uid, updatedAt: serverTimestamp() })
}

/**
 * Moves a task between the two collections with the same id, in one batched
 * write. Moving to shared clears a private categoryId and assigns it as asked:
 * by default it stays its owner's, since a private task is only ever theirs and
 * letting the other person see it shouldn't hand it to them. Moving to
 * private drops the assignee.
 */
export async function setTaskScope(p: Paths, task: Task, scope: Scope, privateCategoryIds: Set<string>, assigneeUid: string | null = p.uid): Promise<void> {
  if (task.scope === scope) return
  const data = stripLocal(task) as unknown as Record<string, unknown>
  if (scope === 'private') {
    delete data.assigneeUid
  } else {
    data.assigneeUid = assigneeUid
    if (task.categoryId && privateCategoryIds.has(task.categoryId)) data.categoryId = null
  }
  data.updatedBy = p.uid
  data.updatedAt = serverTimestamp()
  const batch = writeBatch(db)
  batch.delete(taskRef(p, task.scope, task.id))
  batch.set(taskRef(p, scope, task.id), data)
  await batch.commit()
}

/**
 * One-offs ticked within a range of days, from both collections: what a past
 * week shows, beyond the listener's last 30 days. Repeats and routines are
 * always loaded, their ticks in `completions`.
 */
export async function loadDoneBetween(p: Paths, from: DateStr, to: DateStr): Promise<Task[]> {
  const results = await Promise.all(
    (['shared', 'private'] as Scope[]).map(async (scope) => {
      const snap = await getDocs(query(taskCollection(p, scope), where('done', '==', true), where('doneOn', '>=', from), where('doneOn', '<=', to)))
      return snap.docs.map((d) => ({ ...(d.data() as TaskDoc), id: d.id, scope, pending: false }) as Task)
    }),
  )
  return results.flat()
}

/** Settings' clean-up: ticked one-offs from before a day, from both collections, to delete. */
export async function loadDoneBefore(p: Paths, before: DateStr): Promise<Task[]> {
  const results = await Promise.all(
    (['shared', 'private'] as Scope[]).map(async (scope) => {
      const snap = await getDocs(query(taskCollection(p, scope), where('done', '==', true), where('doneOn', '<', before)))
      return snap.docs.map((d) => ({ ...(d.data() as TaskDoc), id: d.id, scope, pending: false }) as Task)
    }),
  )
  return results.flat()
}

/** The stored fields of a task, without the client-side id, scope and pending flag. */
export function stripLocal(task: Task): TaskDoc {
  const { id: _id, scope: _scope, pending: _pending, ...rest } = task
  const out = { ...rest } as Record<string, unknown>
  for (const k of Object.keys(out)) if (out[k] === undefined) delete out[k]
  return out as unknown as TaskDoc
}
